import { useId, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { DmNotification } from '../../../../services/dmNotifications';
import { formatDateTime, formatShortDate } from '../../../../utils/dates';
import { CollapsibleCell } from '../../shared/CollapsibleCell';
import { NotificationInstruction } from '../NotificationInstruction';
import { NotificationLevelPill } from '../NotificationLevelPill';
import { instructionTexts, notificationTitle } from '../notificationText';
import { NotificationReviewAction } from '../NotificationReviewAction';

interface NotificationCardProps {
    notification: DmNotification;
    onReviewed: (notificationId: string) => Promise<void>;
}

interface TitleRowProps {
    notification: DmNotification;
    // Null when there's nothing to fold: the row is then plain text, not a toggle.
    fold: { expanded: boolean; controls: string; onToggle: () => void } | null;
}

// Title wraps on the left, pill on the right; h-5 holds pill and chevron to the title's first line.
function TitleRow({ notification, fold }: TitleRowProps) {
    const content = (
        <>
            {fold && (
                <span className="flex h-5 flex-shrink-0 items-center text-gray-400">
                    <ChevronRightIcon
                        aria-hidden="true"
                        className={`h-4 w-4 transition-transform motion-reduce:transition-none ${fold.expanded ? 'rotate-90' : ''}`}
                    />
                </span>
            )}
            <span className="min-w-0 flex-1 break-words text-[13px] font-semibold leading-5 text-gray-900">
                {notificationTitle(notification)}
            </span>
            <span className="flex h-5 flex-shrink-0 items-center">
                <NotificationLevelPill level={notification.level} />
            </span>
        </>
    );
    const rowClass = 'flex w-full items-start gap-2 text-left';
    return (
        <h3>
            {fold ? (
                <button
                    type="button"
                    aria-expanded={fold.expanded}
                    aria-controls={fold.controls}
                    onClick={fold.onToggle}
                    className={`${rowClass} rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400`}
                >
                    {content}
                </button>
            ) : (
                <span className={rowClass}>{content}</span>
            )}
        </h3>
    );
}

export function NotificationCard({ notification, onReviewed }: NotificationCardProps) {
    const { patient, team } = instructionTexts(notification);
    const [expanded, setExpanded] = useState(true);
    const contentId = useId();
    const hasContent = Boolean(patient || team);
    const fold = hasContent
        ? { expanded, controls: contentId, onToggle: () => setExpanded((open) => !open) }
        : null;

    return (
        <article className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-colors hover:border-gray-300">
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                <TitleRow notification={notification} fold={fold} />
            </div>
            {hasContent && (
                <div id={contentId} aria-hidden={!expanded}>
                    <CollapsibleCell collapsed={!expanded}>
                        <div className="flex flex-col gap-1 px-4 pt-3">
                            <NotificationInstruction label="Patient:" text={patient} />
                            <NotificationInstruction label="Team:" text={team} />
                        </div>
                    </CollapsibleCell>
                </div>
            )}
            <div className="flex min-w-0 items-center justify-between gap-2 px-4 py-3">
                <time
                    dateTime={notification.createdAt}
                    title={formatDateTime(notification.createdAt)}
                    className="flex-shrink-0 whitespace-nowrap text-xs text-gray-400 tabular-nums"
                >
                    {formatShortDate(notification.createdAt)}
                </time>
                <NotificationReviewAction notification={notification} onReviewed={onReviewed} />
            </div>
        </article>
    );
}
